// 统一的 API 模块 - 支持两种调用方式
// 1. 直接导入函数: import { getTeacherInfo } from '~/api/api'
// 2. 导入 API 对象: import api from '~/api/api'; api.getTeacherInfo()

import { removeOrderIdFromUrl } from "~/utils/common";
import { parseSearchTempResponse } from "~/utils/search-temp";

// 创建统一的 API 函数集合
const createApiMethods = () => {
	const { $axios, $config } = useNuxtApp();
	const cloudDataApiBase = (
		$config?.public?.cloudDataApiBase ||
		$config?.public?.apiBase ||
		""
	).replace(/\/$/, "");
	const h5CloudDataApiBase = (
		$config?.public?.h5CloudDataApiBase ||
		$config?.public?.cloudDataApiBase ||
		$config?.public?.apiBase ||
		""
	).replace(/\/$/, "");
	const trackEventsApiKey = $config?.public?.trackEventsApiKey || "";

	// 通用请求方法
	const request = async config => {
		try {
			const response = await $axios(config);
			return response;
		} catch (error) {
			console.error("API请求错误:", error);
			throw error;
		}
	};

	return {
		// 获取老师信息
		getTeacherInfo: params =>
			request({
				url: "/v1/teacher/get_list_by_uid",
				method: "get",
				params,
			}),

		// 获取老师评价列表
		getEvaluateList: params =>
			request({
				url: "/v1/teacher/evaluate/list",
				method: "get",
				params,
			}),

		// 获取老师服务列表
		getServiceList: params =>
			request({
				url: "/v2/teacher/new_serve",
				method: "get",
				params,
			}),

		// 热门测试排行榜
		getCalculateRank: () =>
			request({
				url: "/v1/product_site/get_calculate_rank",
				method: "get",
			}),

		// 热门问题排行榜
		getQuestionRank: params =>
			request({
				url: "/v1/quick_question/get_question_list",
				method: "get",
				params,
			}),

		// 创建聊天室
		createChatRoom: params =>
			request({
				url: "/api/free_chat/create_room",
				method: "post",
				data: params,
			}),

		// 获取产品分类
		getCategoryList: params =>
			request({
				url: "/v1/product_site/get_category_list",
				method: "get",
				params,
			}),

		// 获取产品分类下的产品
		getCategoryProduct: params =>
			request({
				url: "/v1/product_site/get_category_product",
				method: "get",
				params,
			}),

		// 老师榜单
		getRankingList: params =>
			request({
				url: "/v2/teacher/ranking_list",
				method: "get",
				params,
			}),

		// 咨询分类
		getServiceOriginTeacher: params =>
			request({
				url: "/v2/high/service/origin/teacher",
				method: "get",
				params,
			}),

		// 老师推荐（新版）
		getHighServiceOriginTeacher: params =>
			request({
				url: "/v2/high/service/origin/teacher",
				method: "get",
				params,
			}),

		// 获取老师列表
		getOnlineAskTeacherList: params =>
			request({
				url: "/v1/product_site/get_online_ask_teacher_list",
				method: "get",
				params,
			}),

		// 获取假聊天室固定老師
		getGuideOnlineAskTeacherList: params =>
			request({
				url: "/v1/product_site/get_online_ask_specify_teacher",
				method: "get",
				params,
			}),

		// 获取匹配老师列表
		getMatchTeacherList: params =>
			request({
				url: "/v1/product_site/get_match_teacher_list",
				method: "get",
				params,
			}),

		// 获取老师池老师
		getTeacherPoolTeacher: params =>
			request({
				url: "/v1/product_site/get_teacher_pool_teacher_list",
				method: "get",
				params,
			}),

		// 获取用户详情
		getUserDetail: params =>
			request({
				url: "/v2/user/detail",
				method: "get",
				params,
			}),

		// 获取老师服务
		getTeacherService: params =>
			request({
				url: "/v2/teacher/new_serve",
				method: "get",
				params,
			}),

		// 获取老师服务详情
		getTeacherServiceDetail: params =>
			request({
				url: "/v1/high/service/expensive/info",
				method: "get",
				params,
			}),

		// 获取老师影音
		getTeacherSquare: params =>
			request({
				url: "/v1/product_site/get_teacher_square",
				method: "get",
				params,
			}),

		// 聊天列表
		getFreeChatList: params =>
			request({
				url: "/v2/freechat/lists",
				method: "get",
				params,
			}),

		// 获取用户信息
		getUserInfo: params =>
			request({
				url: "/v1/user/information",
				method: "get",
				params,
			}),

		// 上传图片
		uploadImage: Filedata =>
			request({
				url: "/api/ask/upload/image",
				method: "post",
				data: { Filedata },
			}),

		// 服务下单
		orderConsultant: data => {
			if (!data.return_url && import.meta.client) {
				data.return_url = removeOrderIdFromUrl(window.location.href);
			}
			return request({
				url: "/v1/order/consultant",
				method: "post",
				data,
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
			});
		},

		// 机器测试下单
		orderMachineConsultant: data => {
			if (!data.return_url && import.meta.client) {
				data.return_url = removeOrderIdFromUrl(window.location.href);
			}
			return request({
				url: "/v1/order/machine_calculation",
				method: "post",
				data,
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
			});
		},

		// 抽牌
		drawCard: params =>
			request({
				url: "/v1/live/tarot/draw",
				method: "get",
				params,
			}),

		// 广场详情
		getSquareDetail: params =>
			request({
				url: "/api.service/square/content/detail",
				method: "get",
				params,
			}),

		// 获取广场播客列表
		getSquareAudioList: params =>
			request({
				url: "/api.service/square/content/audio_list",
				method: "get",
				params,
			}),

		// 获取广场视频列表
		getSquareVideoList: params =>
			request({
				url: "/api.service/square/content/video",
				method: "get",
				params,
			}),

		// 支付方式
		getOverseasPayList: params =>
			request({
				url: "/v1/overseas/order/channel",
				method: "get",
				params,
			}),

		// 获取资源位数据
		getResourse: params => {
			return request({
				url: "/v1/home/common/adv_resourse",
				method: "get",
				params,
			});
		},

		// 获取付费测算
		getForecastZone: params =>
			request({
				url: "/v1/home/forecast_zone",
				method: "get",
				params,
			}),

		// 获取关于我们页新闻发布数据
		getAboutUsNewsList: params =>
			request({
				url: "/v1/home/common/adv_resourse",
				method: "get",
				params: {
					class: 142,
					...(params || {}),
				},
				headers: {
					Platform: "74",
					lang: "zh_TW",
				},
			}),

		// 真人算命的咨询分类老师池接口
		getTeacherPools: params =>
			request({
				url: "/v1/product_site/get_teacher_pool_list",
				method: "get",
				params,
			}),

		// 推荐问题
		getRecommendAsk: params =>
			request({
				url: "/v1/freechat/recommend/ask",
				method: "get",
				params,
			}),

		// 高级服务倒计时接口
		getServiceEndTime: () =>
			request({
				url: "/v1/product_site/get_processing_service",
				method: "get",
			}),

		// 获取热门话题
		getHotQuestion: () =>
			request({
				url: "/v1/product_site/get_hot_question",
				method: "get",
			}),

		// 获取老师最新评价
		getTeacherNewEvaluateList: () =>
			request({
				url: "/v1/product_site/get_teacher_new_evaluate_list",
				method: "get",
			}),

		// 获取老师分类
		getTeacherType: () =>
			request({
				url: "/v1/product_site/get_teacher_type",
				method: "get",
			}),

		// 获取对应分类老师
		getTeacherByType: params =>
			request({
				url: "/v1/product_site/get_teacher_by_type",
				method: "get",
				params,
			}),

		// 获取在线测试cms公共配置
		getCmsConfig: (env, channel) =>
			request({
				url: `https://tw-relayfe.seersecret.com/api/acttemp/pj/tw_config?env=${env}`,
				method: "get",
				params: {
					channel,
					lang: "zh-TW",
				},
			}),

		// 发送关注邮件
		sendFollowEmail: params =>
			request({
				url: "/v1/product_site/send_follow_email",
				method: "get",
				params,
			}),

		// 获取APK下载链接
		getDownloadApk: () =>
			request({
				url: "/v1/download/link",
				method: "get",
				params: {
					sys: 0,
				},
			}),

		// 广场列表信息
		getGuanchangList: params =>
			request({
				url: "/v1/product_site/get_square_list",
				method: "get",
				params,
			}),

		// 广场列表详细信息
		getGuanchangDetail: params =>
			request({
				url: "/v1/product_site/get_square_info",
				method: "get",
				params,
			}),

		// 获取品牌特约tab页
		getBrandList: params =>
			request({
				url: "/api/business/enum/get_list_by_key",
				method: "get",
				params,
			}),

		// 播放次数
		playContent: data =>
			request({
				url: "/api.service/square/content/play",
				method: "post",
				data,
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
			}),

		// 焦点推荐分类
		getJiaodianList: params =>
			request({
				url: "/api/business/enum/list",
				method: "get",
				params,
			}),

		// 心里小测试
		getMentalList: () =>
			request({
				url: "/v1/common/get_test_question_list",
				method: "get",
			}),

		// 获取商品列表
		getGoodsList: params =>
			request({
				url: "/api/shop/goods/search",
				method: "get",
				params,
			}),

		// 获取商品列表
		getGoodsTagList: params =>
			request({
				url: "/v1/product_site/goods/get_tag_list",
				method: "get",
				params,
			}),

		// 获取商品分类  1 功效 2 品类
		getGoodsCategoryList: type =>
			request({
				url: "/v1/product_site/goods/get_category_list",
				method: "get",
				params: {
					type,
				},
			}),

		// 获取商品详情
		getGoodsInfo: params =>
			request({
				url: "/api/shop/goods/info",
				method: "get",
				params,
			}),

		// 商品下单
		orderGoods: data =>
			request({
				url: "/api/order/goods",
				method: "post",
				data,
			}),

		// 获取商品订单详情
		getOrderInfo: params =>
			request({
				url: "/v1/product_site/goods/get_order_info",
				method: "get",
				params,
			}),

		// 绑定订单
		bindGoodsOrder: data =>
			request({
				url: "/v1/product_site/goods/bind_order",
				method: "post",
				data,
			}),

		// 获取推荐商品
		getRecommendGoods: params =>
			request({
				url: "/v1/product_site/goods/get_recommend_goods",
				method: "get",
				params,
			}),

		// 获取商品评论
		getGoodsComments: params =>
			request({
				url: "/v1/product_site/goods/get_comments",
				method: "get",
				params,
			}),

		// 获取自动回复
		getOnlineAskAutoReply: params =>
			request({
				url: "/v1/product_site/get_online_ask_auto_reply",
				method: "get",
				params,
			}),

		// 假聊天室老师服务列表
		getGuideTeacherService: params =>
			request({
				url: "/v1/product_site/get_online_ask_teacher_service_list",
				method: "get",
				params,
			}),

		// 发送文本消息到免费聊天室
		sendMessageToChat: data =>
			request({
				url: "/api/free_chat/send_message",
				method: "post",
				data,
			}),

		// 发送圖片消息到免费聊天室
		sendPicToChat: data =>
			request({
				url: "/api/free_chat/send",
				method: "post",
				data,
			}),

		// 人生K线测试
		getResultLifeKLine: params =>
			request({
				url: "/v1/common/life_k_line/get_algorithm_result",
				method: "get",
				params,
			}),

		// 保存记录接口
		submitLifeKLineRecord: data =>
			request({
				url: "/v1/common/life_k_line/store_record",
				method: "post",
				data,
			}),

		// 获取ai结果
		getLifeKLineAIResult: params =>
			request({
				url: "/v1/common/life_k_line/get_ai_result",
				method: "get",
				params,
			}),

		// 人生k线列表
		getLifeKLineList: ({ visitor }) =>
			request({
				url: "/v1/common/life_k_line/list",
				method: "get",
				headers: { visitor },
			}),

		// 获取人生k线历史记录
		getLifeKLineHistoryRecord: params =>
			request({
				url: "/v1/common/life_k_line/get_history_record",
				method: "get",
				params,
			}),

		// 删除人生k线记录
		deleteLifeKLineRecord: data =>
			request({
				url: "/v1/common/life_k_line/delete_record",
				method: "post",
				data,
			}),

		// 更新人生k线记录
		saveLifeKLineRecord: data =>
			request({
				url: "/v1/common/life_k_line/save_record",
				method: "post",
				data,
			}),

		// 点击事件
		attributionClick: data =>
			request({
				url: "/v2/attribution/click",
				method: "post",
				data,
			}),

		// 打开链接
		attributionOpen: data =>
			request({
				url: "/v2/attribution/open",
				method: "post",
				data,
			}),

		// 归因错误上报
		attributionSiteVerifyError: data =>
			request({
				url: "/v2/attribution/site_verify_error",
				method: "post",
				data,
			}),

		getPromotionLink: id =>
			request({
				url: `/v2/promotion_link/${id}`,
				method: "get",
			}),

		getSquareCreatorFeatured: params =>
			request({
				url: "/api.service/square/creator/featured",
				method: "get",
				params,
			}),

		// 获取用户追踪的创作者列表
		getFollowCreatorList: params =>
			request({
				url: "/api.service/square/creator/follow/list",
				method: "get",
				params,
			}),

		// 获取广场影音列表
		getSquareMediaList: params =>
			request({
				url: "/api.service/square/media/list",
				method: "get",
				params,
			}),

		// 获取创作者内容列表
		getCreatorContentList: params =>
			request({
				url: "/api.service/creator/content_list",
				method: "get",
				params,
			}),

		// 获取地区代码列表
		getCountryCode: () =>
			request({
				url: "/v2/merchant/country_code",
				method: "get",
			}),

		// 根据国家代码获取区域列表
		getRegions: countryCode =>
			request({
				url: `/v2/merchant/regions/${countryCode}`,
				method: "get",
			}),

		// 根据经纬度获取附近区域列表
		getMerchantRegionsByLocation: params =>
			request({
				url: "/v2/merchant/regions",
				method: "get",
				params,
			}),

		// 根据地址获取经纬度
		getCoordinates: address =>
			request({
				url: "/v2/merchant/coordinates",
				method: "get",
				params: {
					address,
				},
			}),

		// 提交机构入驻申请
		submitMerchantApply: data =>
			request({
				url: "/v2/merchant/apply",
				method: "post",
				data,
				headers: {
					"Content-Type": "application/json",
				},
			}),

		// 根据 IP 获取用户经纬度
		getIpToLatLng: () =>
			request({
				url: "/v2/common/ip_to_lat_lng",
				method: "get",
			}),

		// 获取机构列表
		getMerchantList: params =>
			request({
				url: "/v2/merchant",
				method: "get",
				params,
			}),

		// 获取机构热门分类
		getMerchantTypes: (config = {}) =>
			request({
				url: "/v2/merchant/types",
				method: "get",
				...config,
			}),

		// 获取短视频分类列表
		getShortPlayClassList: params =>
			request({
				url: "/v1/pgc/short_play/get_class_list",
				method: "get",
				params,
			}),

		// 获取课程列表
		getCourseList: params =>
			request({
				url: "/api.service/course/list",
				method: "get",
				params,
			}),

		// 翻译
		getGoogleTranslation: data =>
			request({
				url: `${$config?.public?.adminApiBase}/v1/common/translation/center`,
				method: "post",
				data: JSON.stringify(data),
				headers: {
					"Content-Type": "application/json",
				},
			}),

		// 获取机构详情
		getMerchantDetail: id =>
			request({
				url: `/v2/merchant/full/${id}`,
				method: "get",
			}),

		// 获取机构详情
		getMerchantShowDetail: (id, headers) =>
			request({
				url: `/v2/merchant/${id}`,
				method: "get",
				headers: headers || {},
			}),

		// 编辑机构入驻申请
		editMerchantApply: (id, data) =>
			request({
				url: `/v2/merchant/${id}`,
				method: "post",
				data,
				headers: {
					"Content-Type": "application/json",
				},
			}),
		// 获取短视频列表
		getShortPlayList: params =>
			request({
				url: "/v1/pgc/short_play/get_list",
				method: "get",
				params,
			}),

		// 打赏下单
		inviteReward: data =>
			request({
				url: "/v1/order/reward",
				method: "post",
				data,
			}),

		// 消耗金币
		consumeCoin: data =>
			request({
				url: "/v1/user/coin/consume",
				method: "post",
				data,
			}),

		// 创建商家服务
		createMerchantService: data =>
			request({
				url: "/v2/merchant/service",
				method: "post",
				data,
				headers: {
					"Content-Type": "application/json",
				},
			}),

		// 编辑商家服务
		editMerchantService: (id, data) =>
			request({
				url: `/v2/merchant/service/${id}`,
				method: "POST",
				data,
				headers: {
					"Content-Type": "application/json",
				},
			}),

		// 获取商家订单列表
		getMerchantOrderList: params =>
			request({
				url: "/v2/merchant/order/list",
				method: "get",
				params,
			}),

		// 获取订单退款详情
		getMerchantOrderRefund: orderId =>
			request({
				url: `/v2/merchant/order/refund/${orderId}`,
				method: "get",
			}),

		// 获取命運投稿案例列表
		getFateSubmissionList: params =>
			request({
				url: "/v1/activity/get_fate_submission_list",
				method: "get",
				params,
			}),

		// 上传命運投稿案例 Excel 文件
		uploadFateSubmissionExcel: data =>
			request({
				url: "/v1/activity/upload_fate_submission_excel",
				method: "post",
				data,
				headers: {
					"Content-Type": "multipart/form-data",
				},
			}),

		// 获取ip老师
		getIpTeacher: params =>
			request({
				url: "/api.service/teacher/ip_teacher",
				method: "get",
				params,
			}),

		// 问答推荐老师列表
		getTeacherAskRecommendList: params =>
			request({
				url: "/v2/teacher/ask_recommend_list",
				method: "get",
				params,
			}),

		// 获取老师推荐热榜
		getTeacherRecommendHot: params =>
			request({
				url: "/api.service/teacher/recommend/hot",
				method: "get",
				params,
			}),

		// 获取IP归属地
		getIpHomeAddress: () =>
			request({
				url: "/v1/common/ip_home_address",
				method: "get",
			}),

		// 冷启动候选标签
		getColdStartCandidateTags: () =>
			request({
				url: `${cloudDataApiBase}/cold-start/candidate-tags`,
				method: "get",
			}),

		// 冷启动用户已选标签
		getColdStartUserSelectedTags: user_id =>
			request({
				url: `${cloudDataApiBase}/cold-start/user-selected-tags`,
				method: "get",
				params: {
					user_id,
				},
			}),

		// 通用埋点上报
		trackEvents: data =>
			request({
				url: `${h5CloudDataApiBase}/track`,
				method: "post",
				data,
				headers: {
					"Content-Type": "application/json",
					apikey: trackEventsApiKey,
				},
			}),

		// 导师讲堂课程列表
		getCourseList: params =>
			request({
				url: "/api.service/course/list",
				method: "get",
				params,
			}),

		// 导师推荐列表
		getTeacherAskRecommendList: params =>
			request({
				url: "/v2/teacher/ask_recommend_list",
				method: "get",
				params,
			}),

		// 广场图文内容
		getSquareContentImageText: params =>
			request({
				url: "/api.service/square/content/image_text",
				method: "get",
				params,
			}),

		// 老师入驻地区选择
		getMentorJoinRegionSelect: (config = {}) =>
			request({
				url: "/v1/common/get_area_list",
				method: "get",
				...config,
			}),

		// 老师属性
		getMentorJoinTeacherAttribute: (config = {}) =>
			request({
				url: "/api.service/teacher/enter/teacher/attribute",
				method: "get",
				...config,
			}),

		// 资料提交
		submitMentorJoin: data =>
			request({
				url: "/api.service/teacher/enter/info/submit",
				method: "post",
				data,
			}),

		// 录音题目
		getMentorJoinListenQuestions: data =>
			request({
				url: `/v1/teacher/enter_exam?apply_id=${data?.apply_id}`,
				method: "get",
			}),

		// 上传录音题目
		submitMentorJoinListenQuestions: data =>
			request({
				url: "/v1/teacher/answer_exam",
				method: "post",
				data,
			}),

		// 调查问卷
		getMentorJoinQuestionnaire: data =>
			request({
				url: `/api.service/teacher/enter/ask/topic?apply_id=${data?.apply_id}`,
				method: "get",
			}),

		// 提交问卷
		submitMentorJoinQuestionnaire: data =>
			request({
				url: "/api.service/teacher/enter/ask/topic/submit",
				method: "post",
				data,
			}),

		// 入驻状态
		getMentorJoinStatus: () =>
			request({
				url: "/api.service/teacher/enter/step/status",
				method: "get",
			}),

		// 首页黄历
		getHomeCalendar: data =>
			request({
				url: `/api.service/alg/bazi/huangli?data=${data}`,
				method: "get",
			}),

		// 黄历专题 - 日历详情（date: yyyyMMdd）
		getHuangliCalendar: params =>
			request({
				url: "/api.service/alg/bazi/huangli",
				method: "get",
				params,
			}),

		// 黄历专题 - 详解
		getHuangliXiangjie: params =>
			request({
				url: "/v1/fate/huangli/detail",
				method: "get",
				params,
			}),

		// 黄历专题 - 择日分类
		getChooseDayType: () =>
			request({
				url: "/v1/fate/zeri/get_category",
				method: "get",
			}),

		// 黄历专题 - 择日列表
		getChooseDayList: params =>
			request({
				url: "/v1/fate/zeri",
				method: "get",
				params,
			}),

		// 首页今日能量
		getHomeEnergy: () =>
			request({
				url: "/v1/fate/new/new_day",
				method: "get",
			}),

		// 今日能量页 - 档案摘要
		getEnergySummary: () =>
			request({
				url: "/v1/fate/new/index",
				method: "get",
			}),

		// 今日能量页 - 日运详情
		getEnergyDetail: params =>
			request({
				url: "/v1/fate/new/new_day",
				method: "get",
				params,
			}),

		// 今日能量页 - 月运
		getMonthFortune: params =>
			request({
				url: "/v1/fate/new/new_month",
				method: "get",
				params,
			}),

		// 今日能量页 - 年运
		getYearFortune: params =>
			request({
				url: "/v1/fate/new/new_year",
				method: "get",
				params,
			}),

		// 首页/能量页咨询问题列表
		getAppIndexQuestionList: () =>
			request({
				url: "/v1/common/get_app_index_question_list",
				method: "get",
			}),

		// 首页人气导师
		getHomeFameTeacher: params =>
			request({
				url: "/v2/high/service/origin/teacher",
				method: "get",
				params,
			}),

		// 全局搜索
		searchTemp: params =>
			request({
				url: "/v1/search/temp",
				method: "get",
				params,
				transformResponse: [parseSearchTempResponse],
			}),

		// 广场详情-相关视频推荐
		getSquareRecommend: params =>
			request({
				url: "/api.service/square/creator/featured",
				method: "get",
				params,
			}),

		// 广场评论区
		getSquareCommentList: params =>
			request({
				url: "/api.service/square/reply/list",
				method: "get",
				params,
			}),

		// 广场点赞
		likeSquare: data =>
			request({
				url: "/api.service/square/content/zan",
				method: "post",
				data,
			}),

		// 广场评论点赞
		likeSquareComment: data =>
			request({
				url: "/api.service/square/reply/zan",
				method: "post",
				data,
			}),

		// 广场评论
		createSquareComment: data =>
			request({
				url: "/api.service/square/content/reply",
				method: "post",
				data,
			}),

		// 老师相关图文
		getTeacherRecommend: params =>
			request({
				url: "/api.service/creator/content_list",
				method: "get",
				params,
			}),

		// 广场追踪
		trackSquare: data =>
			request({
				url: "/v1/user/follow",
				method: "post",
				data,
			}),

		// 广场 banner 资源位
		getSquareBanner: params =>
			request({
				url: "/api.service/square/media/list",
				method: "get",
				params,
				headers: {
					Platform: "71",
				},
			}),
	};
};

// 懒加载 API 实例
let apiInstance = null;
const getApiInstance = () => {
	if (!apiInstance) {
		apiInstance = createApiMethods();
	}
	return apiInstance;
};

// 导出单独的 API 函数（支持直接导入）
export const getTeacherInfo = params => getApiInstance().getTeacherInfo(params);
export const getEvaluateList = params => getApiInstance().getEvaluateList(params);
export const getServiceList = params => getApiInstance().getServiceList(params);
export const getCalculateRank = () => getApiInstance().getCalculateRank();
export const getQuestionRank = params => getApiInstance().getQuestionRank(params);
export const createChatRoom = params => getApiInstance().createChatRoom(params);
export const getCategoryList = params => getApiInstance().getCategoryList(params);
export const getCategoryProduct = params => getApiInstance().getCategoryProduct(params);
export const getRankingList = params => getApiInstance().getRankingList(params);
export const getServiceOriginTeacher = params => getApiInstance().getServiceOriginTeacher(params);
export const getHighServiceOriginTeacher = params =>
	getApiInstance().getHighServiceOriginTeacher(params);
export const getTeacherPoolTeacher = params => getApiInstance().getTeacherPoolTeacher(params);
export const getUserDetail = params => getApiInstance().getUserDetail(params);
export const getTeacherService = params => getApiInstance().getTeacherService(params);
export const getTeacherServiceDetail = params => getApiInstance().getTeacherServiceDetail(params);
export const getTeacherSquare = params => getApiInstance().getTeacherSquare(params);
export const getFreeChatList = params => getApiInstance().getFreeChatList(params);
export const getUserInfo = params => getApiInstance().getUserInfo(params);
export const uploadImage = Filedata => getApiInstance().uploadImage(Filedata);
export const orderConsultant = data => getApiInstance().orderConsultant(data);
export const orderMachineConsultant = data => getApiInstance().orderMachineConsultant(data);
export const drawCard = params => getApiInstance().drawCard(params);
export const getSquareDetail = params => getApiInstance().getSquareDetail(params);
export const getSquareAudioList = params => getApiInstance().getSquareAudioList(params);
export const getSquareVideoList = params => getApiInstance().getSquareVideoList(params);
export const getOverseasPayList = params => getApiInstance().getOverseasPayList(params);
export const getResourse = params => getApiInstance().getResourse(params);
export const getForecastZone = params => getApiInstance().getForecastZone(params);
export const getAboutUsNewsList = params => getApiInstance().getAboutUsNewsList(params);
export const getTeacherPools = params => getApiInstance().getTeacherPools(params);
export const getRecommendAsk = params => getApiInstance().getRecommendAsk(params);
export const getServiceEndTime = () => getApiInstance().getServiceEndTime();
export const getHotQuestion = () => getApiInstance().getHotQuestion();
export const getTeacherNewEvaluateList = () => getApiInstance().getTeacherNewEvaluateList();
export const getTeacherType = () => getApiInstance().getTeacherType();
export const getTeacherByType = params => getApiInstance().getTeacherByType(params);
export const getCmsConfig = (env, channel) => getApiInstance().getCmsConfig(env, channel);
export const sendFollowEmail = params => getApiInstance().sendFollowEmail(params);
export const getDownloadApk = () => getApiInstance().getDownloadApk();
export const getGuanchangList = params => getApiInstance().getGuanchangList(params);
export const getGuanchangDetail = params => getApiInstance().getGuanchangDetail(params);
export const getBrandList = params => getApiInstance().getBrandList(params);
export const playContent = data => getApiInstance().playContent(data);
export const getJiaodianList = params => getApiInstance().getJiaodianList(params);
export const getMentalList = () => getApiInstance().getMentalList();
export const getGoodsList = params => getApiInstance().getGoodsList(params);
export const getGoodsCategoryList = tpye => getApiInstance().getGoodsCategoryList(tpye);
export const getGoodsInfo = params => getApiInstance().getGoodsInfo(params);
export const orderGoods = data => getApiInstance().orderGoods(data);
export const getOrderInfo = params => getApiInstance().getOrderInfo(params);
export const bindGoodsOrder = data => getApiInstance().bindGoodsOrder(data);
export const getRecommendGoods = params => getApiInstance().getRecommendGoods(params);
export const getGoodsTagList = params => getApiInstance().getGoodsTagList(params);
export const getGoodsComments = params => getApiInstance().getGoodsComments(params);
export const getOnlineAskTeacherList = params => getApiInstance().getOnlineAskTeacherList(params);
export const getMatchTeacherList = params => getApiInstance().getMatchTeacherList(params);
export const getOnlineAskAutoReply = params => getApiInstance().getOnlineAskAutoReply(params);
export const getGuideTeacherService = params => getApiInstance().getGuideTeacherService(params);
export const sendMessageToChat = params => getApiInstance().sendMessageToChat(params);
export const sendPicToChat = params => getApiInstance().sendPicToChat(params);
export const getGuideOnlineAskTeacherList = params =>
	getApiInstance().getGuideOnlineAskTeacherList(params);
export const getResultLifeKLine = data => getApiInstance().getResultLifeKLine(data);
export const submitLifeKLineRecord = data => getApiInstance().submitLifeKLineRecord(data);
export const getLifeKLineAIResult = params => getApiInstance().getLifeKLineAIResult(params);
export const getLifeKLineList = params => getApiInstance().getLifeKLineList(params);
export const getLifeKLineHistoryRecord = params =>
	getApiInstance().getLifeKLineHistoryRecord(params);
export const deleteLifeKLineRecord = data => getApiInstance().deleteLifeKLineRecord(data);
export const saveLifeKLineRecord = data => getApiInstance().saveLifeKLineRecord(data);
export const attributionClick = data => getApiInstance().attributionClick(data);
export const attributionOpen = data => getApiInstance().attributionOpen(data);
export const attributionSiteVerifyError = data => getApiInstance().attributionSiteVerifyError(data);
export const getSquareCreatorFeatured = params => getApiInstance().getSquareCreatorFeatured(params);
export const getFollowCreatorList = params => getApiInstance().getFollowCreatorList(params);
export const getCreatorContentList = params => getApiInstance().getCreatorContentList(params);
export const getPromotionLink = id => getApiInstance().getPromotionLink(id);
export const getCountryCode = () => getApiInstance().getCountryCode();
export const getRegions = countryCode => getApiInstance().getRegions(countryCode);
export const getMerchantRegionsByLocation = params =>
	getApiInstance().getMerchantRegionsByLocation(params);
export const getCoordinates = address => getApiInstance().getCoordinates(address);
export const submitMerchantApply = data => getApiInstance().submitMerchantApply(data);
export const getIpToLatLng = () => getApiInstance().getIpToLatLng();
export const getMerchantList = params => getApiInstance().getMerchantList(params);
export const getMerchantTypes = (config = {}) => getApiInstance().getMerchantTypes(config);
export const getGoogleTranslation = data => getApiInstance().getGoogleTranslation(data);
export const getMerchantDetail = id => getApiInstance().getMerchantDetail(id);
export const getMerchantShowDetail = (id, headers) =>
	getApiInstance().getMerchantShowDetail(id, headers);
export const editMerchantApply = (id, data) => getApiInstance().editMerchantApply(id, data);
export const getShortPlayClassList = params => getApiInstance().getShortPlayClassList(params);
export const getShortPlayList = params => getApiInstance().getShortPlayList(params);
export const getCourseList = params => getApiInstance().getCourseList(params);
export const inviteReward = data => getApiInstance().inviteReward(data);
export const consumeCoin = data => getApiInstance().consumeCoin(data);
export const createMerchantService = data => getApiInstance().createMerchantService(data);
export const editMerchantService = (id, data) => getApiInstance().editMerchantService(id, data);
export const getMerchantOrderList = params => getApiInstance().getMerchantOrderList(params);
export const getMerchantOrderRefund = orderId => getApiInstance().getMerchantOrderRefund(orderId);
export const getFateSubmissionList = params => getApiInstance().getFateSubmissionList(params);
export const uploadFateSubmissionExcel = data => getApiInstance().uploadFateSubmissionExcel(data);
export const getIpTeacher = params => getApiInstance().getIpTeacher(params);
export const getTeacherAskRecommendList = params =>
	getApiInstance().getTeacherAskRecommendList(params);
export const getTeacherRecommendHot = params => getApiInstance().getTeacherRecommendHot(params);
export const getIpHomeAddress = () => getApiInstance().getIpHomeAddress();
export const getColdStartCandidateTags = () => getApiInstance().getColdStartCandidateTags();
export const getColdStartUserSelectedTags = user_id =>
	getApiInstance().getColdStartUserSelectedTags(user_id);
export const trackEvents = data => getApiInstance().trackEvents(data);
export const getSquareMediaList = params => getApiInstance().getSquareMediaList(params);
export const getSquareContentImageText = params =>
	getApiInstance().getSquareContentImageText(params);
export const getMentorJoinRegionSelect = config =>
	getApiInstance().getMentorJoinRegionSelect(config);
export const getMentorJoinTeacherAttribute = config =>
	getApiInstance().getMentorJoinTeacherAttribute(config);
export const submitMentorJoin = data => getApiInstance().submitMentorJoin(data);
export const getMentorJoinListenQuestions = data =>
	getApiInstance().getMentorJoinListenQuestions(data);
export const submitMentorJoinListenQuestions = data =>
	getApiInstance().submitMentorJoinListenQuestions(data);
export const getMentorJoinQuestionnaire = data => getApiInstance().getMentorJoinQuestionnaire(data);
export const submitMentorJoinQuestionnaire = data =>
	getApiInstance().submitMentorJoinQuestionnaire(data);
export const getMentorJoinStatus = () => getApiInstance().getMentorJoinStatus();
export const getHomeCalendar = data => getApiInstance().getHomeCalendar(data);
export const getHuangliCalendar = params => getApiInstance().getHuangliCalendar(params);
export const getHuangliXiangjie = params => getApiInstance().getHuangliXiangjie(params);
export const getChooseDayType = () => getApiInstance().getChooseDayType();
export const getChooseDayList = params => getApiInstance().getChooseDayList(params);
export const getHomeEnergy = () => getApiInstance().getHomeEnergy();
export const getEnergySummary = () => getApiInstance().getEnergySummary();
export const getEnergyDetail = params => getApiInstance().getEnergyDetail(params);
export const getMonthFortune = params => getApiInstance().getMonthFortune(params);
export const getYearFortune = params => getApiInstance().getYearFortune(params);
export const getAppIndexQuestionList = () => getApiInstance().getAppIndexQuestionList();
export const getHomeFameTeacher = params => getApiInstance().getHomeFameTeacher(params);
export const searchTemp = params => getApiInstance().searchTemp(params);
export const getSquareRecommend = params => getApiInstance().getSquareRecommend(params);
export const getSquareCommentList = params => getApiInstance().getSquareCommentList(params);
export const likeSquare = data => getApiInstance().likeSquare(data);
export const likeSquareComment = data => getApiInstance().likeSquareComment(data);
export const createSquareComment = data => getApiInstance().createSquareComment(data);
export const getTeacherRecommend = params => getApiInstance().getTeacherRecommend(params);
export const trackSquare = data => getApiInstance().trackSquare(data);
export const getSquareBanner = params => getApiInstance().getSquareBanner(params);
// 默认导出 API 对象（支持 api.方法名 调用）
const api = {
	get: () => getApiInstance(),
};

// 使用 Proxy 来实现 api.方法名 的调用方式
const apiProxy = new Proxy(api, {
	get(target, prop) {
		if (prop === "get") {
			return target.get;
		}
		const instance = getApiInstance();
		if (prop in instance) {
			return instance[prop];
		}
		return undefined;
	},
});

export default apiProxy;
